/** Imports */
import {z} from 'zod';
import {APP_LOCALES, type AppLocale, isAppLocale as isConfiguredAppLocale} from '@/i18n/appLocales';
import {type OrganizationLanguageCode, zOrganizationLanguageCodeSchema} from '@/mongoSchemas/Locale/LanguageCode.zod';


/** Zod schemas */
export const zAppLocale = z.enum(APP_LOCALES);


/** Types */
export type {AppLocale};
export type Locale = OrganizationLanguageCode;


/** Constants */
export const currentPathCookieName = 'mediafox-current-path';
const localeToLanguageCodeMap: Record<AppLocale, OrganizationLanguageCode> = {
    en: 'EN',
    de: 'DE',
    it: 'IT',
    es: 'ES',
    fr: 'FR',
};
const languageCodeToLocaleMap: Record<OrganizationLanguageCode, AppLocale> = {
    EN: 'en',
    DE: 'de',
    IT: 'it',
    ES: 'es',
    FR: 'fr',
};


/** Helpers */
export function isAppLocale(locale: string): locale is AppLocale {
    return isConfiguredAppLocale(locale);
}

export function localeToLanguageCode(locale: string): OrganizationLanguageCode | null {
    if (!isAppLocale(locale)) return null;
    return localeToLanguageCodeMap[locale];
}

export function parseLanguageCode(locale: string): OrganizationLanguageCode {
    return zOrganizationLanguageCodeSchema
        .parse(locale.toUpperCase());
}

export function languageCodeToLocale(languageCode: string | null | undefined): AppLocale | null {
    const parsedLanguageCode = zOrganizationLanguageCodeSchema
        .safeParse(languageCode);
    if (!parsedLanguageCode.success) return null;
    return languageCodeToLocaleMap[parsedLanguageCode.data];
}

export function replaceLocaleInPath(pathname: string, locale: AppLocale): string {
    const [pathWithoutQuery, queryString] = pathname.split('?');
    const normalizedPathname = (pathWithoutQuery.startsWith('/'))
        ? pathWithoutQuery
        : `/${pathWithoutQuery}`;
    const segments = normalizedPathname.split('/');
    if ((segments.length > 1) && isAppLocale(segments[1] || '')) segments[1] = locale;
    else segments.splice(1, 0, locale);
    const nextPathname = segments.join('/').replace(/\/+/g, '/');
    return (queryString)
        ? `${nextPathname}?${queryString}`
        : nextPathname;
}
